import React, { FC, PropsWithChildren, ReactElement } from 'react';
import { ItemIds } from './itemIds';

/**
 * The JSON representation of an item
 */
export interface ItemDefinition<T extends ItemIds, K = unknown> {
  type: T;
  id: string;
  start?: number;
  position?: number;
  values: K;
  items: ItemDefinition<ItemIds>[];
  editorMode?: unknown;
}

/**
 * Helper type to define the properties of the render function
 */
export type RenderProps<K> = PropsWithChildren<
  K & {
    id: string;
    type?: string;
    parent?: Item<ItemIds>;
    self: Item<ItemIds, K>;
  }
>;

/**
 * Base class for template items. It helps with the parsing of the JSON data and the rendering of the item and its children
 */
export abstract class Item<T extends ItemIds, K = unknown> {
  public children?: Item<ItemIds>[];
  protected definition: ItemDefinition<T, K>;
  protected parent?: Item<ItemIds>;

  public get type() {
    return this.definition.type;
  }

  public get id() {
    return this.definition.id;
  }

  protected get values() {
    return this.definition.values;
  }

  public get start() {
    return this.definition.start || 0.0;
  }

  constructor(definition: ItemDefinition<T, K>, parent?: Item<ItemIds>) {
    this.definition = definition;
    this.parent = parent;
    this.validate();
    this.children = (definition.items || []).map(i => {
      const itemClass = registry[i.type];
      if (itemClass) {
        return new itemClass(i, this);
      }
      throw new Error(`could not find item class for [${i.type}]`);
    });
  }

  private comp: FC;

  /**
   * Returns a component that renders the item and its children
   */
  component() {
    if (!this.comp) {
      const c: FC<RenderProps<K>> = ({ children }) => (
        <this.render
          {...this.values}
          id={this.id}
          parent={this.parent}
          self={this}
        >
          {this.children?.map(c => {
            const Comp = c.component();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return <Comp key={c.id} type={c.type} id={c.id} {...c.values} />;
          })}
          {children}
        </this.render>
      );
      this.comp = c.bind(this);
    }

    return this.comp;
  }

  /**
   * Implement this to validate that the item is configured properly.
   * In particular, check the parent type and the children types.
   * Any bad config should throw an ItemValidationError
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected validate() {}

  /**
   * This is a React function component that will be called with the item's properties
   */
  protected abstract render(
    props: RenderProps<K>
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ReactElement<any, any> | null;
}

type ItemClass<T extends ItemIds, K> = new (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  definition: ItemDefinition<T, K> & any,
  parent: Item<ItemIds>
) => Item<T, K>;

/**
 * Associates item types with the classes used to implement them
 */
export const registry: { [K in ItemIds]?: ItemClass<K, unknown> } = {};

/**
 * Custom exception type to report a bad use or configuration of an item
 */
export class ItemValidationError extends Error {}
